// LAP-646 — Card "Vue globale" : KPI + ratio pro/perso + delta YoY
import React from 'react'
import { Box, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined'
import CardHeader from '../../Investments/_shared/CardHeader'
import Delta from '../../Investments/_shared/Delta'
import EmptyHelp from './EmptyHelp'
import { invTheme } from '../../Investments/_shared/theme'

function MetricTile({ icon, label, value, accent }) {
  return (
    <Box
      sx={{
        flex: 1,
        p: 2,
        borderRadius: 2,
        backgroundColor: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          right: -10,
          top: -10,
          width: 60,
          height: 60,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${accent}40, transparent 70%)`,
        }}
      />
      <Box display="flex" alignItems="center" mb={1} sx={{ color: accent }}>
        {icon}
        <Box sx={{ ...invTheme.subtitle, ml: 0.75, color: 'rgba(255,255,255,0.55)' }}>
          {label}
        </Box>
      </Box>
      <Box sx={{ fontSize: 28, fontWeight: 700, color: '#fff', lineHeight: 1.1 }}>{value}</Box>
    </Box>
  )
}

export default function OverviewCard({ data, loading }) {
  const { t } = useTranslation('layout')
  if (!loading && !data) {
    return (
      <Box display="flex" flexDirection="column" height="100%">
        <CardHeader
          icon={<PeopleOutlinedIcon fontSize="small" />}
          title={t('social.overview.title', 'Vue globale')}
          subtitle={t('social.overview.subtitle', 'Total · Interactions · Ratio')}
        />
        <EmptyHelp
          icon={<PeopleOutlinedIcon sx={{ fontSize: 22 }} />}
          title={t('social.empty.overview.title', 'Pas encore de vue globale')}
          description={t(
            'social.empty.overview.description',
            'Ajoute des contacts puis crée des événements dans ton calendrier en leur associant un participant : les KPI (contacts actifs, interactions du mois, ratio pro / perso) se calculent automatiquement à partir de ces données.',
          )}
          actions={[
            { key: 'contacts', label: t('social.empty.cta.addContact', 'Ajouter un contact') },
            { key: 'calendar', label: t('social.empty.cta.createEvent', 'Créer un événement') },
          ]}
        />
      </Box>
    )
  }
  const o = data || {}
  return (
    <Box display="flex" flexDirection="column" height="100%">
      <CardHeader
        icon={<PeopleOutlinedIcon fontSize="small" />}
        title={t('social.overview.title', 'Vue globale')}
        subtitle={t('social.overview.subtitle', 'Total · Interactions · Ratio')}
        delta={
          o.yoy_change_pct !== undefined && o.yoy_change_pct !== null
            ? { value: o.yoy_change_pct, unit: '%' }
            : null
        }
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mt={2}>
        <MetricTile
          icon={<PeopleOutlinedIcon fontSize="small" />}
          label={t('social.overview.totalContacts', 'Contacts actifs')}
          value={o.total_contacts ?? '—'}
          accent={invTheme.accents.market}
        />
        <MetricTile
          icon={<GroupsOutlinedIcon fontSize="small" />}
          label={t('social.overview.interactionsMonth', 'Interactions ce mois-ci')}
          value={o.interactions_this_month ?? '—'}
          accent={invTheme.accents.repartition}
        />
        <MetricTile
          icon={<HandshakeOutlinedIcon fontSize="small" />}
          label={t('social.overview.ratio', 'Pro / Perso')}
          value={
            o.ratio_pro !== undefined && o.ratio_perso !== undefined
              ? `${o.ratio_pro}% / ${o.ratio_perso}%`
              : '—'
          }
          accent={invTheme.accents.opportunity}
        />
      </Stack>
      {o.yoy_change_pct !== undefined && o.yoy_change_pct !== null && (
        <Box
          mt={2}
          display="flex"
          alignItems="center"
          gap={1}
          sx={{
            color: 'rgba(255,255,255,0.6)',
            fontSize: 12,
            pt: 1,
            borderTop: '1px dashed rgba(255,255,255,0.08)',
          }}
        >
          <Box>{t('social.overview.yoy', 'Comparaison N vs N-1')} :</Box>
          <Delta value={o.yoy_change_pct} unit="%" />
        </Box>
      )}
    </Box>
  )
}
